/**
 * Session-error classification for the Apollo error link (PRO-141).
 *
 * The backend (graphql_jwt) uses ONE message — "You do not have permission to
 * perform this action" — for two very different situations:
 *
 * 1. an UNAUTHENTICATED call hitting a `@login_required` resolver — a real
 *    401, the session is dead and the app must return to /login;
 * 2. a legacy resolver denying an AUTHENTICATED user something — a 403-class
 *    permission error that must surface inline and NEVER end the session.
 *
 * The FE used to treat every occurrence as (1), so a wrongful permission
 * denial force-logged the user out (PRO-141 defect #2). The tiebreaker is the
 * session token itself: when a JWT is present in storage the account was
 * signed in, so the message is a permission denial, not a missing session.
 * (A dead-but-present token fails with graphql_jwt's dedicated messages —
 * "Signature has expired" / "Error decoding signature" — not this one.)
 *
 * Kept as a pure function so it is unit-testable without the Apollo client.
 */

/** graphql_jwt's reserved unauthenticated-call message (exact match). */
export const UNAUTHENTICATED_MESSAGE =
  'You do not have permission to perform this action'

// mfa-email-login: a wrong / expired / locked MFA code surfaces from the
// backend as an ORDINARY GraphQL business error — a human-readable `message`
// over HTTP 200, never a transport 401 and never the reserved unauthenticated
// message — so the classifier already returns 'none' for it via the default
// path below and the session is never ended. It is shown inline on the code
// step. (An earlier machine-`extensions.code` scheme was removed: the backend
// surfaces `message`, not `extensions.code`, so that path was dead.)

/**
 * SCIM deprovisioning (PRODUCT-117): when an IdP deactivates a user the backend
 * rejects their still-present token with graphql_jwt's dedicated
 * `JSONWebTokenError('User is disabled')`. This is a DISTINCT message from the
 * ambiguous permission one above and is always a dead session — the account
 * must be ejected, not kept.
 */
export const USER_DISABLED_MESSAGE = 'User is disabled'

/**
 * graphql_jwt's messages for a stored token that can no longer sign anyone
 * in. The boot-time verifyToken (checkTokenRefreshTokenOrLogout in
 * App/router.tsx) clears such a token on page load, but a token can also die
 * while a tab is open: it expires, a logout in another tab refuses it, or the
 * backend rotates its signing key (it reports those last two as expired too).
 * Every request then fails until the user reloads, so once the boot check has
 * run, these end the session like a 401.
 */
export const DEAD_TOKEN_MESSAGES = [
  'Signature has expired',
  'Error decoding signature',
]

export type SessionErrorKind =
  /** Real authentication failure — clear the session, go to /login. */
  | 'logout'
  /** Authorization (403-class) denial — surface it, keep the session. */
  | 'permission-denied'
  /** Not a session-relevant error. */
  | 'none'

export const classifySessionError = ({
  networkStatusCode,
  graphQLErrorMessages,
  hasSessionToken,
  sessionVerified = false,
}: {
  networkStatusCode?: number
  graphQLErrorMessages: string[]
  hasSessionToken: boolean
  /** Whether the boot-time verifyToken has run; until then it owns dead tokens. */
  sessionVerified?: boolean
}): SessionErrorKind => {
  // A transport-level 401 is always an authentication failure.
  if (networkStatusCode === 401) return 'logout'

  // SCIM deprovisioning (PRODUCT-117): a deactivated account is a dead session
  // that must be EJECTED even though its token is still present — so this sits
  // ahead of the permission branch and ignores `hasSessionToken`. Matched on
  // the whole distinctive phrase (case- and trailing-punctuation-tolerant) so
  // it survives minor backend wording without ever swallowing an ordinary
  // permission denial (the PRO-141 regression this file exists to prevent).
  if (
    graphQLErrorMessages.some(
      (message) =>
        message
          .trim()
          .replace(/[\s.]+$/, '')
          .toLowerCase() === USER_DISABLED_MESSAGE.toLowerCase()
    )
  )
    return 'logout'

  if (
    sessionVerified &&
    graphQLErrorMessages.some((message) =>
      DEAD_TOKEN_MESSAGES.includes(message)
    )
  )
    return 'logout'

  if (!graphQLErrorMessages.includes(UNAUTHENTICATED_MESSAGE)) return 'none'

  // The ambiguous message: no token -> nothing to preserve, treat as the
  // 401 it is; live token -> a permission denial that must not end the
  // session.
  return hasSessionToken ? 'permission-denied' : 'logout'
}
